import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import { Import, Plus, SlidersHorizontal } from "lucide-react";
import {
  CanAccess,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button, buttonVariants } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import type { DealCard, LeadSource, Sale } from "../types";
import { daysInStage, needsActionNow } from "./cardStatus";
import { LostList } from "./LostList";
import { SaleDialog } from "./SaleDialog";
import { TagBadges } from "./LeadTags";
import { useTags } from "../tags/useTags";
import {
  FUNNEL_SORTS,
  type FunnelSort,
  fullName,
  toneClass,
  useCardStatusText,
  useCardSubtitle,
  useFunnelFilters,
  useFunnelStages,
  usePermissions,
  useRefreshFunnel,
} from "./hooks";
import { FunnelShell, NativeSelect } from "./shared";

const SOURCES: LeadSource[] = [
  "facebook",
  "instagram",
  "site",
  "phone",
  "instagram_direct",
  "messenger",
  "referral",
  "repeat",
  "walk_in",
  "import",
  "manual",
  "other",
];

export const FunnelPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const permissions = usePermissions();
  const { salesId, source, tag, sort, set, reset, filter, params } =
    useFunnelFilters();
  const { data: tags = [] } = useTags();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilters =
    [salesId, source, tag].filter(Boolean).length + (sort !== "newest" ? 1 : 0);
  const segment = params.get("segment") === "lost" ? "lost" : "active";
  const seesEveryone = permissions.includes("leads.all");

  const { data: sales } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "first_name", order: "ASC" },
    },
    { enabled: seesEveryone },
  );
  const { total: activeTotal } = useGetList<DealCard>("deal_cards", {
    filter: { ...filter, "stage@neq": "lost" },
    pagination: { page: 1, perPage: 1 },
  });
  const { total: lostTotal } = useGetList<DealCard>("deal_cards", {
    filter: { ...filter, stage: "lost" },
    pagination: { page: 1, perPage: 1 },
  });

  const filters = (
    <>
      {seesEveryone ? (
        <NativeSelect
          aria-label={translate("crm.funnel.all_managers")}
          value={salesId}
          onChange={(event) => set("manager", event.target.value)}
        >
          <option value="">{translate("crm.funnel.all_managers")}</option>
          {sales?.map((sale) => (
            <option key={sale.id} value={sale.id}>
              {sale.first_name} {sale.last_name}
            </option>
          ))}
        </NativeSelect>
      ) : null}
      <NativeSelect
        aria-label={translate("crm.funnel.all_sources")}
        value={source}
        onChange={(event) => set("source", event.target.value)}
      >
        <option value="">{translate("crm.funnel.all_sources")}</option>
        {SOURCES.map((value) => (
          <option key={value} value={value}>
            {translate(`resources.contacts.sources.${value}`)}
          </option>
        ))}
      </NativeSelect>
      {tags.length ? (
        <NativeSelect
          aria-label={translate("crm.funnel.tags.all")}
          value={tag}
          onChange={(event) => set("tag", event.target.value)}
        >
          <option value="">{translate("crm.funnel.tags.all")}</option>
          {tags.map((value) => (
            <option key={value.id} value={value.id}>
              {value.name}
            </option>
          ))}
        </NativeSelect>
      ) : null}
      {segment === "active" ? (
        <NativeSelect
          aria-label={translate("crm.funnel.sort.label")}
          value={sort}
          onChange={(event) =>
            set(
              "sort",
              event.target.value === "newest" ? "" : event.target.value,
            )
          }
        >
          {(Object.keys(FUNNEL_SORTS) as FunnelSort[]).map((value) => (
            <option key={value} value={value}>
              {translate(`crm.funnel.sort.${value}`)}
            </option>
          ))}
        </NativeSelect>
      ) : null}
    </>
  );

  const segments = (
    <div
      role="tablist"
      className={cn(
        "rounded-xl border bg-card p-1 text-sm",
        isMobile ? "flex min-w-0 flex-1" : "inline-flex",
      )}
    >
      {(["active", "lost"] as const).map((value) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={segment === value}
          onClick={() => set("segment", value === "lost" ? "lost" : "")}
          className={cn(
            "rounded-lg py-1.5 font-semibold whitespace-nowrap transition-colors",
            isMobile ? "flex-1 px-1.5 text-[13px]" : "px-4",
            segment === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {translate(`crm.funnel.${value}`)}{" "}
          <span className="opacity-70">
            {(value === "active" ? activeTotal : lostTotal) ?? 0}
          </span>
        </button>
      ))}
    </div>
  );

  const newLead = (
    <div className="flex gap-2">
      {!isMobile && (
        <CanAccess resource="import" action="create">
          <Link
            to="/funnel/import"
            className={buttonVariants({ variant: "outline" })}
          >
            <Import />
            {translate("crm.navigation.import_leads")}
          </Link>
        </CanAccess>
      )}
      <Link
        to="/funnel/new"
        className={buttonVariants({ size: isMobile ? "icon" : "default" })}
        aria-label={translate("crm.funnel.new_lead")}
        title={translate("crm.funnel.new_lead")}
      >
        <Plus />
        {isMobile ? null : translate("crm.funnel.new_lead")}
      </Link>
    </div>
  );

  return (
    <FunnelShell
      title={translate("crm.funnel.title")}
      actions={
        isMobile ? (
          <div className="flex w-full items-center gap-1.5">
            {segments}
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="relative shrink-0"
              aria-label={translate("crm.funnel.filters")}
              onClick={() => setFiltersOpen(true)}
            >
              <SlidersHorizontal />
              {activeFilters ? (
                <span className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-[#d89a2b] text-[11px] font-bold text-white">
                  {activeFilters}
                </span>
              ) : null}
            </Button>
            {newLead}
          </div>
        ) : (
          <>
            {segments}
            <div className="ml-auto">{newLead}</div>
          </>
        )
      }
    >
      {/* Filters get a row of their own on a computer, a sheet on a phone */}
      {isMobile ? (
        <Drawer open={filtersOpen} onOpenChange={setFiltersOpen}>
          <DrawerContent>
            <div className="flex flex-col gap-4 p-5 pb-8">
              <DrawerTitle className="text-lg font-bold">
                {translate("crm.funnel.filters")}
              </DrawerTitle>
              <div className="flex flex-col gap-3 [&>select]:h-11 [&>select]:w-full [&>select]:text-base">
                {filters}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={!activeFilters}
                  onClick={() => reset(["manager", "source", "tag", "sort"])}
                >
                  {translate("crm.funnel.filters_reset")}
                </Button>
                <Button
                  type="button"
                  size="lg"
                  onClick={() => setFiltersOpen(false)}
                >
                  {translate("crm.funnel.filters_done")}
                </Button>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        <div className="flex flex-wrap items-center gap-2 [&>select]:w-48 [&>select]:min-w-0">
          {filters}
        </div>
      )}
      {segment === "lost" ? (
        <LostList filter={filter} />
      ) : isMobile ? (
        <MobileBoard filter={filter} sort={sort} />
      ) : (
        <DesktopBoard filter={filter} sort={sort} />
      )}
    </FunnelShell>
  );
};

FunnelPage.path = "/funnel";

const useActiveCards = (filter: Record<string, unknown>, sort: FunnelSort) =>
  useGetList<DealCard>("deal_cards", {
    filter: { ...filter, "stage@neq": "lost" },
    pagination: { page: 1, perPage: 500 },
    sort: FUNNEL_SORTS[sort] ?? FUNNEL_SORTS.newest,
  });

type BoardProps = { filter: Record<string, unknown>; sort: FunnelSort };

const DesktopBoard = ({ filter, sort }: BoardProps) => {
  const stages = useFunnelStages();
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { data } = useActiveCards(filter, sort);
  const refresh = useRefreshFunnel();
  // Local copy so a dropped card moves at once, before the server answers
  const [cards, setCards] = useState<DealCard[]>([]);
  const [saleCard, setSaleCard] = useState<DealCard | null>(null);
  useEffect(() => setCards(data ?? []), [data]);

  const byStage = useMemo(() => {
    const groups: Record<string, DealCard[]> = {};
    for (const stage of stages) groups[stage.value] = [];
    for (const card of cards) groups[card.stage]?.push(card);
    return groups;
  }, [cards, stages]);

  const onDragEnd = async ({ destination, draggableId }: DropResult) => {
    if (!destination) return;
    const card = cards.find((c) => String(c.id) === draggableId);
    if (!card || card.stage === destination.droppableId) return;
    const stage = destination.droppableId;
    // a sale needs its amount: ask first, move after
    if (stage === "won" && !card.amount) {
      setSaleCard(card);
      return;
    }
    setCards(
      cards.map((c) =>
        c.id === card.id
          ? { ...c, stage, stage_changed_at: new Date().toISOString() }
          : c,
      ),
    );
    try {
      await dataProvider.update("deals", {
        id: card.id,
        data: { stage },
        previousData: card,
      });
      notify("crm.funnel.lead.stage_changed");
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    }
    refresh();
  };

  return (
    <>
      {saleCard ? (
        <SaleDialog
          deal={saleCard}
          mode="won"
          open
          onOpenChange={(open) => !open && setSaleCard(null)}
          onSaved={refresh}
        />
      ) : null}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-4">
          {stages.map((stage) => (
            <div
              key={stage.value}
              className="flex min-w-60 flex-1 basis-0 flex-col rounded-xl border-t-4 p-2"
              style={{
                borderTopColor: stage.color,
                backgroundColor: `${stage.color ?? "#65705f"}14`,
              }}
            >
              <div className="flex items-center gap-2 px-1 pb-2 pt-1">
                <span
                  className="size-2.5 rounded-full"
                  style={{ backgroundColor: stage.color }}
                />
                <span className="font-bold">{stage.label}</span>
                <span
                  className="ml-auto rounded-full px-2 text-xs font-bold text-white"
                  style={{ backgroundColor: stage.color }}
                >
                  {byStage[stage.value]?.length ?? 0}
                </span>
              </div>
              <Droppable droppableId={stage.value}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "flex min-h-24 flex-1 flex-col gap-2 rounded-lg",
                      snapshot.isDraggingOver && "bg-card/60",
                    )}
                  >
                    {byStage[stage.value]?.map((card, index) => (
                      <Draggable
                        key={card.id}
                        draggableId={String(card.id)}
                        index={index}
                      >
                        {(drag) => (
                          <div
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            {...drag.dragHandleProps}
                          >
                            <FunnelCard card={card} />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {byStage[stage.value]?.length === 0 ? (
                      <p className="px-1 py-2 text-xs text-muted-foreground">
                        {translate("crm.funnel.empty_stage")}
                      </p>
                    ) : null}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
    </>
  );
};

const MobileBoard = ({ filter, sort }: BoardProps) => {
  const stages = useFunnelStages();
  const translate = useTranslate();
  const { data } = useActiveCards(filter, sort);
  const [current, setCurrent] = useState(stages[0]?.value);
  const counts = (stage: string) =>
    data?.filter((card) => card.stage === stage).length ?? 0;
  const cards = data?.filter((card) => card.stage === current) ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
        {stages.map((stage) => (
          <button
            key={stage.value}
            type="button"
            onClick={() => setCurrent(stage.value)}
            className={cn(
              "shrink-0 rounded-full border-t-[3px] px-3 py-1.5 text-sm font-semibold",
              current === stage.value
                ? "bg-primary text-primary-foreground"
                : "bg-card",
            )}
            style={{ borderTopColor: stage.color }}
          >
            {stage.label} · {counts(stage.value)}
          </button>
        ))}
      </div>
      {cards.map((card) => (
        <FunnelCard key={card.id} card={card} compact />
      ))}
      {cards.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.funnel.empty_stage")}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        {translate("crm.funnel.mobile_hint")}
      </p>
    </div>
  );
};

export const FunnelCard = ({
  card,
  compact = false,
}: {
  card: DealCard;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const statusText = useCardStatusText();
  const subtitle = useCardSubtitle();
  const now = new Date();
  const status = statusText(card, now);
  const days = daysInStage(card.stage_changed_at, now);
  // untouched for 15+ minutes in working hours
  const urgent = needsActionNow(card, now);
  return (
    <Link
      to={`/funnel/${card.id}`}
      className={cn(
        "block rounded-xl border bg-card p-3 text-foreground no-underline shadow-xs transition-colors hover:border-primary/40",
        urgent && "border-destructive/60 ring-2 ring-destructive/25",
      )}
    >
      <TagBadges ids={card.tags} className="mb-1.5" />
      <div className={cn(compact && "flex items-start justify-between gap-2")}>
        <div className={cn("font-bold", compact && "min-w-0 truncate")}>
          {fullName(card)}
        </div>
        {compact && status ? (
          <div
            className={cn(
              "shrink-0 pt-0.5 text-xs font-bold whitespace-nowrap",
              toneClass[status.tone],
            )}
          >
            {status.text}
          </div>
        ) : null}
      </div>
      <div className="text-xs text-muted-foreground">{subtitle(card)}</div>
      {!compact && status ? (
        <div className={cn("mt-1 text-xs font-bold", toneClass[status.tone])}>
          {status.text}
        </div>
      ) : null}
      {!compact ? (
        <div className="mt-1 text-[11px] text-muted-foreground">
          {days < 1
            ? translate("crm.funnel.in_stage_today")
            : translate("crm.funnel.in_stage", { count: days })}
        </div>
      ) : null}
    </Link>
  );
};
